import React, { useEffect, useMemo, useState } from 'react'
import { useUnit } from 'effector-react'
import { Box, classnames, Unit, useFetch } from '@centra/centra-ui'
import { buildAccountSurveyCounterCategoryAssociations, resetAccountSurveyCounter } from '../../../utils/counters'
import {
  AccountSurveyCounterCategoryAssociation,
  FETCH_ACCOUNT_SURVEY_COUNTER_TIMEOUT,
  PostTaskSurveyAnswer,
  TaskStatus,
  TaskSurveyFormData,
} from '../../../store/taskManager/model'
import styles from './TaskSurvey.module.scss'
import TaskSurveyPopup from './_Popup'
import { postTaskSurveyAnswer } from '../../../utils/taskSurvey'
import { taskSurveyEffectorModel } from '../../../effectorStores/taskSurvey'
import { TaskSurveyAnswerStatus } from '../../../effectorStores/taskSurvey/model'
import { isMobileDevice } from 'utils'
const TaskSurvey = () => {
  const breakpoint = isMobileDevice ? 'MOBILE' : 'DESKTOP'
  /** Из-за того, что у нас временно стоит <meta name="viewport" width=1280px, /> в главном html, брейкпоинты корректно не работают */
  if (isMobileDevice) return null

  const {
    fetchAccountSurveyCounterEvent,
    fetchLatestDoneTaskEvent,
    fetchLatestDoneTaskSurveyAnswerEvent,
    setTaskSurveyFormOpenStatusEvent,
    setTaskSurveyFormSubmittingEvent,
    setTaskSurveyButtonStatusEvent,
    $accountSurveyCounterStore,
    $currentTaskStore,
    $currentTaskSurveyAnswerStore,
    $latestDoneTaskStore,
    $latestDoneTaskSurveyAnswerStore,
    $taskSurveyFormOpenStatusStore,
    $taskSurveyFormSubmittingStore,
  } = useUnit(taskSurveyEffectorModel)

  const [isOpenSurveyPopup, setOpenSurveyPopup] = useState(false)

  const backdropClasses = classnames(
    styles.surveyBackDrop,
    $taskSurveyFormOpenStatusStore.isOpen && styles.surveyBackDropActive
  )

  const fetchPostSurveyAnswer = useFetch({
    cb: postTaskSurveyAnswer,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при отправке оценки',
    },
    successMessage: {
      title: 'Ваша оценка отправлена',
      content: 'Спасибо за ваш ответ!',
    },
  })

  const fetchPostSurveyAnswerSilent = useFetch({
    cb: postTaskSurveyAnswer,
  })

  const resetSurveyCounter = useFetch({
    cb: resetAccountSurveyCounter,
  })

  const openSurveyPopupConditions: {
    isHasConditions: boolean
    maxCounter: AccountSurveyCounterCategoryAssociation | null
  } = useMemo(() => {
    const initial = {
      isHasConditions: false,
      maxCounter: null,
    }
    if ($accountSurveyCounterStore && !$taskSurveyFormOpenStatusStore.isOpen) {
      const counterFields = buildAccountSurveyCounterCategoryAssociations($accountSurveyCounterStore)
      const chargedCounters = counterFields.filter(counter => counter.count >= counter.chargeCount)

      return {
        isHasConditions: !!chargedCounters.length,
        maxCounter: chargedCounters.length
          ? chargedCounters.reduce((prev, current) => (prev.count > current.count ? prev : current))
          : null,
      }
    }
    return initial
  }, [$accountSurveyCounterStore])

  const task = $taskSurveyFormOpenStatusStore.taskStore === 'current' ? $currentTaskStore : $latestDoneTaskStore
  const taskSurveyStatus = useMemo(() => {
    if ($latestDoneTaskSurveyAnswerStore) return $latestDoneTaskSurveyAnswerStore

    return task?.surveyAnswerId
      ? ({
          id: task.surveyAnswerId,
          taskId: task.id,
          isPopupClosed: task.isSurveyPopupClosed,
          isSurveySuccess: task.isSurveySuccess,
          isFormOpened: task.isSurveyFormOpened,
        } as unknown as TaskSurveyAnswerStatus)
      : null
  }, [task, $latestDoneTaskSurveyAnswerStore])

  useEffect(() => {
    try {
      fetchAccountSurveyCounterEvent()
    } catch (e) {
      console.error('Ошибка загрузки данных счетчика', e)
    } finally {
      const intervalCounterFetch = setInterval(() => {
        fetchAccountSurveyCounterEvent()
      }, FETCH_ACCOUNT_SURVEY_COUNTER_TIMEOUT)
      return () => clearInterval(intervalCounterFetch)
    }
  }, [])

  useEffect(() => {
    if (openSurveyPopupConditions.isHasConditions && openSurveyPopupConditions.maxCounter) {
      fetchLatestDoneTaskEvent({
        predicates: [
          {
            field: 'statusId',
            operator: 'EQUALS',
            values: [TaskStatus.DONE],
          },
          {
            field: 'categoryId',
            operator: 'EQUALS',
            values: [openSurveyPopupConditions.maxCounter.categoryId],
          },
          {
            field: 'isSurveyPopupAvailable',
            operator: 'EQUALS',
            values: [true],
          },
        ],
      })
    }
  }, [openSurveyPopupConditions])

  useEffect(() => {
    try {
      if ($latestDoneTaskStore) {
        fetchLatestDoneTaskSurveyAnswerEvent({ taskId: $latestDoneTaskStore.id })
      }
    } catch (e) {
      console.error('Ошибка загрузка данных последней завершенной задачи', e)
    }
  }, [$latestDoneTaskStore])

  useEffect(() => {
    /**
     * Показываем попап, если есть:
     * - условия по счетчику (достаточное количество выполненных задач в категории)
     * - отсутствует прохождение опроса и попап не был закрыт пользователем
     */
    setOpenSurveyPopup(
      !!(
        openSurveyPopupConditions.isHasConditions &&
        openSurveyPopupConditions.maxCounter &&
        !taskSurveyStatus?.isPopupClosed &&
        !taskSurveyStatus?.isSurveySuccess
      )
    )
  }, [openSurveyPopupConditions, taskSurveyStatus])

  useEffect(() => {
    setTaskSurveyButtonStatusEvent(
      !!(
        $currentTaskSurveyAnswerStore &&
        $currentTaskSurveyAnswerStore.isPopupClosed &&
        !$currentTaskSurveyAnswerStore.isSurveySuccess
      )
    )
  }, [$currentTaskSurveyAnswerStore])

  useEffect(() => {
    if (fetchPostSurveyAnswerSilent.result && $latestDoneTaskStore) {
      fetchLatestDoneTaskSurveyAnswerEvent({ taskId: $latestDoneTaskStore.id })
    }
  }, [fetchPostSurveyAnswerSilent.result])

  const closeFormAndSetLatestTask = () => {
    setTaskSurveyFormOpenStatusEvent({ isOpen: false, taskStore: 'latest' })
  }

  const handleFullClose = () => {
    setOpenSurveyPopup(false)
    setTaskSurveyFormSubmittingEvent(false)
    setTaskSurveyButtonStatusEvent(false)
    closeFormAndSetLatestTask()
  }

  const handleCloseSurveyPopup = () => {
    if (!task) return
    const data = {
      taskId: task.id,
      accountManagerId: task.accountManagerId,
      isPopupClosed: true,
      breakpoint,
    } as PostTaskSurveyAnswer
    fetchPostSurveyAnswerSilent.fetch(data, taskSurveyStatus)
    setOpenSurveyPopup(false)
    closeFormAndSetLatestTask()
    resetSurveyCounter.fetch(task.categoryId)
    setTaskSurveyButtonStatusEvent(true)
  }

  const handleSubmitForm = (formData: TaskSurveyFormData) => {
    setTaskSurveyFormSubmittingEvent(true)
    const { taskCategoryId, ...answerData } = formData
    const data = {
      ...answerData,
      breakpoint,
    } as PostTaskSurveyAnswer
    fetchPostSurveyAnswer.fetch(data, taskSurveyStatus)
    resetSurveyCounter.fetch(taskCategoryId)
    handleFullClose()
  }

  return (
    <Unit id={'TaskSurveyPanel'} entityCtx={{ entityId: 'task', objectId: `${task?.id}` }}>
      <Box>
        <Box className={backdropClasses} />
        <TaskSurveyPopup
          task={task}
          isOpenPopup={isOpenSurveyPopup}
          isOpenForm={$taskSurveyFormOpenStatusStore.isOpen}
          submitting={$taskSurveyFormSubmittingStore}
          onClose={handleCloseSurveyPopup}
          onSubmit={handleSubmitForm}
        />
      </Box>
    </Unit>
  )
}

export default TaskSurvey
